// Feature flags for the source-game feature pass (docs/FEATURES-BRIEF.md).
// Everything player-visible that a priority adds is gated here, default on.
// Sim rules (penalties, catalogue) are not flags: turning a flag off hides
// the new UI and leaves the numbers as the sim computed them.

export const FEATURES = {
  /** P1: the tip breakdown card on results (reasons × counts, outcome copy). */
  tipBreakdown: true,
  /** P1: task tiles go amber at 90% and green at 98%; the knock-off board shows the tip you'd forgo. */
  taskThresholds: true,
  /** P2: the chute's extra collision circle (blocks, and bills "chute dragged along the …"). The game passes it to createSim; a bare createSim leaves it off (DECISIONS.md). */
  chuteFootprint: true,
  /** P2: the chute-side / catcher chip per mower on the loadout. */
  chuteIcon: true,
  /** P3: boards draw task lists from the subsets (70% with mowing, the rest tidy-ups and clearances). The game passes it to generateBoard; a bare generateBoard keeps the original lists (DECISIONS.md). */
  taskSubsets: true,
  /** P3: a one-line story on every contract (card and loadout). */
  contractStory: true,
  /** P3: loadout notes — soft for gear the job won't use, hard per task with no tool aboard; a no-mow job heads out without a mower. */
  loadoutNotes: true,
  /** P3: the Good fit / Slow going / Not enough gear badge on the contract card. */
  fitBadge: true,
  /** P4: the HQ Crew section (hire, assign, payouts), crew jobs settled when you finish yours, and gear out with the crew locked in the loadout. */
  staff: true,
  /** P5: the stripe line on results (score, tier copy) and its tip bonus (+1/+3/+5 points, never deducts), applied by the shell with `withStripeBonus`. */
  stripeScore: true,
  /** Crossed stripes: a later pass across an earlier one keeps it underneath (grid.stripe2), so checkerboards and diamonds show four tones, and the results name the pattern (+1/+2 tip points for a tidy one). The game passes it to createSim; a bare createSim keeps one-layer stripes. */
  crossStripes: true,
  /** P5: on the player's first two contracts the dashed suggested first pass becomes a full headland lap (and island laps) that stays up while mowing. 2D renderer only for now. */
  headlandHint: true,
  /** P6: debris rules — beds and deck/step footprints count, only the rake clears a bed, catcher 100% toward a clear, the off-property nudge and council line; "Clear leaves" / "Collect & bag" labels. The game passes it to createSim; a bare createSim keeps the old scoring (DECISIONS.md). */
  debrisRules: true,
  /** P7: battery tools run on charge (Recharge A$1 in the service bay, the 15% HUD warning, flat = stops) and wear ×1.6; powered hand tools wear on the trigger. The game passes it to createSim and the crew; a bare createSim keeps the old sim (DECISIONS.md). */
  battery: true,
  /** P8: yards change between visits — the save keeps a visit list per layout key; a yard worked before gets 1–2 new mutations and the card says "New since last time: …". The game passes it to generateBoard (and street contracts); a bare generateBoard is unchanged (DECISIONS.md). */
  revisits: true,
  // Feature P9 — small things that carry the vibe. The sim ones are SimOptions /
  // BoardOptions the game passes; a bare createSim / generateBoard is unchanged (DECISIONS.md).
  /** P9: ground checks spawn the named items (stick … sunnies), collecting shows the name, a wallet pays the "honest bloke" A$5. */
  groundItems: true,
  /** P9: matted tyre tracks behind the wheels, fading over 30 s (turf canvas). */
  tyreTracks: true,
  /** P9: the ZT-137 pivots on the spot in both steering modes with a wider rear swing; a passive tip on its first pivot. */
  zeroTurn: true,
  /** P9: trimming a job that didn't ask for it earns a one-off A$10 goodwill and +1 rep. */
  extraMile: true,
  /** P9: layouts with a `slope` drift the mower downhill a little at speed. */
  slopeDrift: true,
  /**
   * P9: the trimmer cuts to ~15 mm, so snipping inside the mow area scalps below range.
   * OFF (owner, 2026-09-27: "too difficult"): the snipper leaves lawn at the job's
   * normal height everywhere. The mechanic stays in vibe.ts behind this flag.
   */
  trimOvercut: false,
  /** P9: passable props (hose, edging, small rocks) bump instead of block; loose stone edging scatters. */
  passableProps: true,
  /** P9: some yards haven't been mowed since Easter: double length, slower going, fee ×1.3. */
  neglectedGrass: true,
  /** P9: trimming beside paving throws clippings back on it until you blow again. */
  blowBack: true,
  /** P9: a rotating one-liner tip on the Contracts screen. */
  boardTips: true,
  /** P9 refinement: one Shop item 10–20% off per day, seeded by the date; buy() honours it. */
  shopSale: true,
  /** P10: company identity — logo mark, signwriting font and operator kit in setup and Settings; the mark and font on the HQ board, the invoice and the ute; the kit on the 2D operator. Off: name and colour only, the original figure. */
  identity: true,
};

export type FeatureKey = keyof typeof FEATURES;
